/* ──────────────────────────────────────────────────────────────
 *  useInputStore — packaging + target input fields
 *
 *  Holds every value the user can edit on the Input Panel (Card 1).
 *  All numbers are nullable — `null` means "not yet entered".
 * ────────────────────────────────────────────────────────────── */

import { create } from 'zustand';

/* ── Packaging inputs ─────────────────────────────────────── */

export interface PackagingInputs {
  coreLength: number | null;
  outerSleeveLength: number | null;
  controlArmId: number | null;
  coreId: number | null;
  coreOdMin: number | null;
  coreOdMax: number | null;
  hotOd: number | null;
  /** C1-specific collar OD. */
  collarOd: number | null;
  h: number | null;
}

/* ── Target inputs (per-stiffness parameter) ──────────────── */

export interface TargetInputs {
  /** Target stiffness values keyed by param name (e.g. "Cx", "Cz"). */
  targets: Record<string, number | null>;
  /** Test amplitudes keyed by param name. */
  amplitudes: Record<string, number | null>;
  /** Linear travel values keyed by field key (e.g. "axial_c1", "radial_plus", "radial_minus"). */
  linearTravel: Record<string, number | null>;
}

/* ── Full store shape ─────────────────────────────────────── */

interface InputState {
  packaging: PackagingInputs;
  target: TargetInputs;
  sleeveThickness: number;
  shoreA: number;
  /** Compound family selection. */
  compoundFamily: string;
  /** BYD preset datasheet (or null for non-BYD types). */
  bydPreset: string | null;
  /** SOR string identifier (for export labelling). */
  sorString: string;
  /** Auto-calculated minimum contact area (derived, not shown in UI). */
  calculatedMinContactArea: number | null;
  /** Whether compute has been run at least once with current inputs. */
  computeDirty: boolean;

  /* ── Actions ──────────────────────────────────────────── */
  setPackagingField: <K extends keyof PackagingInputs>(
    key: K,
    value: PackagingInputs[K],
  ) => void;
  setPackaging: (fields: Partial<PackagingInputs>) => void;
  setTarget: (param: string, value: number | null) => void;
  setAmplitude: (param: string, value: number | null) => void;
  setLinearTravel: (key: string, value: number | null) => void;
  setTargets: (targets: Record<string, number | null>) => void;
  setAmplitudes: (amplitudes: Record<string, number | null>) => void;
  setSleeveThickness: (value: number) => void;
  setShoreA: (value: number) => void;
  setCompoundFamily: (value: string) => void;
  setBydPreset: (preset: string | null) => void;
  setSorString: (sor: string) => void;
  setCalculatedMinContactArea: (v: number | null) => void;
  markClean: () => void;
  markDirty: () => void;
  resetInputs: () => void;
}

const INITIAL_PACKAGING: PackagingInputs = {
  coreLength: null,
  outerSleeveLength: null,
  controlArmId: null,
  coreId: null,
  coreOdMin: null,
  coreOdMax: null,
  hotOd: null,
  collarOd: null,
  h: null,
};

const INITIAL_TARGETS: TargetInputs = {
  targets: {},
  amplitudes: {},
  linearTravel: {},
};

export const useInputStore = create<InputState>()((set) => ({
  packaging: { ...INITIAL_PACKAGING },
  target: { ...INITIAL_TARGETS },
  sleeveThickness: 2.0,
  shoreA: 55,
  compoundFamily: 'NR-C023A',
  bydPreset: null,
  sorString: '',
  calculatedMinContactArea: null,
  computeDirty: true,

  setPackagingField: (key, value) =>
    set((s) => ({
      packaging: { ...s.packaging, [key]: value },
      computeDirty: true,
    })),

  setPackaging: (fields) =>
    set((s) => ({
      packaging: { ...s.packaging, ...fields },
      computeDirty: true,
    })),

  setTarget: (param, value) =>
    set((s) => ({
      target: {
        ...s.target,
        targets: { ...s.target.targets, [param]: value },
      },
      computeDirty: true,
    })),

  setAmplitude: (param, value) =>
    set((s) => ({
      target: {
        ...s.target,
        amplitudes: { ...s.target.amplitudes, [param]: value },
      },
      computeDirty: true,
    })),

  setLinearTravel: (key: string, value: number | null) =>
    set((s) => ({
      target: {
        ...s.target,
        linearTravel: { ...s.target.linearTravel, [key]: value },
      },
      computeDirty: true,
    })),

  setTargets: (targets) =>
    set((s) => ({
      target: { ...s.target, targets },
      computeDirty: true,
    })),

  setAmplitudes: (amplitudes) =>
    set((s) => ({
      target: { ...s.target, amplitudes },
      computeDirty: true,
    })),

  setSleeveThickness: (value) =>
    set({ sleeveThickness: value, computeDirty: true }),

  setShoreA: (value) => set({ shoreA: value, computeDirty: true }),

  setCompoundFamily: (value: string) => set({ compoundFamily: value, computeDirty: true }),

  setBydPreset: (preset) => set({ bydPreset: preset }),

  setSorString: (sor) => set({ sorString: sor }),

  setCalculatedMinContactArea: (v) => set({ calculatedMinContactArea: v }),

  markClean: () => set({ computeDirty: false }),

  markDirty: () => set({ computeDirty: true }),

  resetInputs: () =>
    set({
      packaging: { ...INITIAL_PACKAGING },
      target: { ...INITIAL_TARGETS },
      sleeveThickness: 2.0,
      shoreA: 55,
      compoundFamily: 'NR-C023A',
      bydPreset: null,
      sorString: '',
      calculatedMinContactArea: null,
      computeDirty: true,
    }),
}));
